import { createContext, useContext, useEffect, useState } from "react";
import { AZ } from "@/lib/i18n.az";
import { PL } from "@/lib/i18n.pl";

const EXTRA = { az: AZ, pl: PL };
const detectLang = () => {
  const b = (navigator.language || "en").slice(0, 2).toLowerCase();
  return ["tr", "az", "pl"].includes(b) ? b : "en";
};

export const LANGS = [
  { code: "en", label: "EN", name: "English" },
  { code: "tr", label: "TR", name: "Türkçe" },
  { code: "az", label: "AZ", name: "Azərbaycan" },
  { code: "pl", label: "PL", name: "Polski" },
];

const DICT = {
  // ----------------- HEADER -----------------
  "nav.services": { en: "Services", tr: "Hizmetler" },
  "nav.pricing": { en: "Pricing", tr: "Ücretler" },
  "nav.request_viewing": { en: "Request Viewing", tr: "Görüntüleme İste" },
  "nav.why_us": { en: "Why Us", tr: "Neden Biz" },
  "nav.faq": { en: "FAQ", tr: "SSS" },
  "lang.Turkish": { en: "Turkish", tr: "Türkçe" },
  "lang.English": { en: "English", tr: "İngilizce" },
  "lang.Azerbaijani": { en: "Azerbaijani", tr: "Azerbaycanca" },
  "lang.Polish": { en: "Polish", tr: "Lehçe" },
  "proc.step": { en: "STEP", tr: "ADIM" },
  "meta.title": { en: "Unity Real Estate — Warsaw Flats, Houses & Relocation for Newcomers", tr: "Unity Real Estate — Varşova'da Daire, Ev ve Taşınma Desteği" },
  "nav.owner": { en: "Are you the owner?", tr: "Ev sahibi misiniz?" },
  "hero.owner_cta": { en: "Own a flat? We'll find your tenant", tr: "Ev sahibi misiniz? Kiracınızı bulalım" },
  "nav.contact": { en: "Contact", tr: "İletişim" },
  "common.whatsapp": { en: "WhatsApp", tr: "WhatsApp" },
  "common.call": { en: "Call", tr: "Ara" },

  // ----------------- HERO -----------------
  "hero.location": { en: "Warsaw · Poland", tr: "Varşova · Polonya" },
  "hero.live": { en: "Reply in 1 hour", tr: "1 saat içinde cevap" },
  "hero.title.1": { en: "Your new home in Warsaw,", tr: "Varşova'da yeni eviniz —" },
  "hero.title.2": { en: "without the headaches.", tr: "stressiz ve hiç dert etmeden." },
  "hero.subtitle": {
    en: "Whether you're moving to Warsaw for the first time, already here and looking to change flats, or need to find a replacement tenant for your current lease — we handle the search, the paperwork and the Polish. In your language.",
    tr: "İster Varşova'ya ilk kez taşınıyor olun, ister halihazırda burada olup daire değiştirmek isteyin, ister kiranızı erken bitirmek için yerinize bir kiracı bulmak isteyin — biz daireyi de, evrakları da, Lehçeyi de hallediyoruz. Hem de kendi dilinizde.",
  },
  "hero.cta_whatsapp": { en: "Chat on WhatsApp", tr: "WhatsApp'tan yazın" },
  "hero.trust_stars": {
    en: "Trusted by 300+ families · Turkish & Azerbaijani clients welcomed daily",
    tr: "300+ ailenin güvendiği isim · Türk ve Azerbaycanlı müşteriler her gün burada",
  },
  "hero.stats.families": { en: "families placed", tr: "aileye yardım ettik" },
  "hero.stats.time_label": { en: "Avg. time to key", tr: "Anahtara ortalama süre" },
  "hero.stats.time_value": { en: "5 days", tr: "5 gün" },
  "hero.stats.docs_label": { en: "Document assistance", tr: "Evrak desteği" },
  "hero.stats.docs_value": { en: "Included", tr: "Dahildir" },
  "hero.stats.lang_label": { en: "Languages spoken", tr: "Konuşulan dil" },
  "hero.stats.districts_label": { en: "Districts covered", tr: "Hizmet bölgeleri" },
  "hero.stats.districts_value": {
    en: "Śródmieście, Mokotów, Wola +12",
    tr: "Śródmieście, Mokotów, Wola +12",
  },
  "hero.stats.quote": {
    en: "\"They handled my PESEL, landlord contract and move-in in under a week.\"",
    tr: "\"PESEL, kira sözleşmesi ve taşınma işlemlerimi bir haftada hallettiler.\"",
  },
  "hero.stats.quote_author": {
    en: "— Ayşe, Istanbul → Warsaw",
    tr: "— Ayşe, İstanbul → Varşova",
  },
  "hero.scroll": { en: "scroll", tr: "kaydır" },

  // ----------------- AUDIENCES -----------------
  "aud.eyebrow": { en: "Who we help", tr: "Kimlere yardım ediyoruz" },
  "aud.title.1": { en: "Three journeys,", tr: "Üç farklı yolculuk," },
  "aud.title.2": { en: "one trusted team.", tr: "tek güvenilir ekip." },
  "aud.blurb": {
    en: "Unity helps anyone dealing with a Warsaw flat — from your first day in Poland to the day you hand the keys back.",
    tr: "Polonya'daki ilk gününüzden anahtarı teslim ettiğiniz güne kadar Varşova'da daireyle ilgili her şeyde yanınızdayız.",
  },
  "aud.new.title": { en: "Moving to Warsaw", tr: "Varşova'ya taşınıyorum" },
  "aud.new.text": {
    en: "New to Poland and need a flat, PESEL, lease and everything in between? Start here — we guide you end-to-end.",
    tr: "Polonya'ya yeni geldiniz ve daire, PESEL, kira sözleşmesi ve aradaki her şeye mi ihtiyacınız var? Baştan sona biz yanınızdayız.",
  },
  "aud.new.msg": {
    en: "Hi! I'm moving to Warsaw and I need help finding a flat and with documents.",
    tr: "Merhaba! Varşova'ya taşınıyorum, daire bulma ve evrak konusunda yardıma ihtiyacım var.",
  },
  "aud.change.title": { en: "Already in Warsaw?", tr: "Zaten Varşova'da mısınız?" },
  "aud.change.text": {
    en: "If you're already living here and want to change your flat — better location, bigger place, lower rent — we'll find your next one.",
    tr: "Burada yaşıyorsanız ve daire değiştirmek istiyorsanız — daha iyi konum, daha büyük yer ya da daha uygun kira — bir sonrakini biz bulalım.",
  },
  "aud.change.msg": {
    en: "Hi! I'm already in Warsaw and I'd like help finding a new flat.",
    tr: "Merhaba! Zaten Varşova'dayım, yeni bir daire bulmama yardımcı olur musunuz?",
  },
  "aud.replace.title": { en: "Need a replacement tenant?", tr: "Yerinize kiracı mı arıyorsunuz?" },
  "aud.replace.text": {
    en: "Leaving Warsaw early and need someone to take over your lease? We'll find a replacement tenant and handle the handover with your landlord.",
    tr: "Varşova'dan erken ayrılıp kiranızı devralacak birini mi arıyorsunuz? Yerinize kiracı buluruz, ev sahibinizle teslim sürecini biz yönetiriz.",
  },
  "aud.replace.msg": {
    en: "Hi! I need to find a replacement tenant for my current lease in Warsaw. Can you help?",
    tr: "Merhaba! Varşova'daki mevcut kiramı devralacak bir kiracı bulmam gerekiyor, yardımcı olabilir misiniz?",
  },

  // ----------------- REQUEST VIEWING -----------------
  "rv.eyebrow": { en: "Request a viewing", tr: "Görüntüleme isteği" },
  "rv.title.1": { en: "Tell us what you need —", tr: "Ne istediğinizi söyleyin —" },
  "rv.title.2": { en: "we'll find the match.", tr: "uygun olanı biz bulalım." },
  "rv.blurb": {
    en: "Fill the three fields and we'll open WhatsApp with your request pre-written. Reply in Turkish, English, Azerbaijani or Polish — we usually get back within an hour.",
    tr: "Üç alanı doldurun, WhatsApp'ı hazır mesajınızla açıyoruz. Türkçe, İngilizce, Azerbaycanca veya Lehçe yanıt verin — genellikle bir saat içinde döneriz.",
  },
  "rv.benefit.1": { en: "Verified listings only", tr: "Sadece doğrulanmış ilanlar" },
  "rv.benefit.2": { en: "No upfront agency fees", tr: "Peşin emlakçılık ücreti yok" },
  "rv.benefit.3": { en: "Document help included", tr: "Evrak yardımı dahil" },
  "rv.f.name": { en: "Your name (optional)", tr: "Adınız (isteğe bağlı)" },
  "rv.f.name.ph": { en: "e.g. Ayşe", tr: "ör. Ayşe" },
  "rv.f.type": { en: "Property type", tr: "Konut tipi" },
  "rv.f.type.ph": { en: "Choose type", tr: "Tip seçin" },
  "rv.f.district": { en: "District (pick one or more)", tr: "Semt (bir veya birden fazla)" },
  "rv.f.district.ph": { en: "Choose district(s)", tr: "Semt seçin" },
  "rv.f.district.summary": {
    en: "districts selected",
    tr: "semt seçildi",
  },
  "rv.f.budget": { en: "Budget (PLN / month)", tr: "Bütçe (PLN / ay)" },
  "rv.f.budget.ph": { en: "e.g. 3500", tr: "ör. 3500" },
  "rv.f.date": { en: "Move-in date (optional)", tr: "Taşınma tarihi (isteğe bağlı)" },
  "rv.f.date.ph": { en: "Pick a date", tr: "Tarih seçin" },
  "rv.submit": { en: "Send request on WhatsApp", tr: "WhatsApp ile gönder" },
  "rv.no_store": {
    en: "No form submissions are stored — your request is sent straight to WhatsApp.",
    tr: "Hiçbir form bilgisi saklanmaz — talebiniz doğrudan WhatsApp'a gönderilir.",
  },
  "rv.err": {
    en: "Please pick at least one district, a property type and your budget.",
    tr: "Lütfen en az bir semt, bir konut tipi ve bütçenizi girin.",
  },
  "rv.opening": {
    en: "Opening WhatsApp with your request…",
    tr: "WhatsApp talebinizle açılıyor…",
  },
  "rv.msg.hi": { en: "Hi!", tr: "Merhaba!" },
  "rv.msg.hi_name": { en: "Hi, I'm {name}.", tr: "Merhaba, ben {name}." },
  "rv.msg.intro": {
    en: "I'd like to request a viewing with Unity Real Estate:",
    tr: "Unity Real Estate'ten bir görüntüleme talep etmek istiyorum:",
  },
  "rv.msg.district": { en: "District", tr: "Semt" },
  "rv.msg.districts": { en: "Districts", tr: "Semtler" },
  "rv.msg.type": { en: "Property type", tr: "Konut tipi" },
  "rv.msg.budget": { en: "Budget", tr: "Bütçe" },
  "rv.msg.move_in": { en: "Move-in", tr: "Taşınma" },
  "rv.msg.flexible": { en: "Flexible", tr: "Esnek" },
  "rv.msg.outro": {
    en: "Please let me know available options. Thanks!",
    tr: "Lütfen müsait seçenekleri benimle paylaşır mısınız? Teşekkürler!",
  },

  // Property types
  "type.room": { en: "Room (in shared flat)", tr: "Oda (paylaşımlı dairede)" },
  "type.studio": { en: "Studio", tr: "Stüdyo daire" },
  "type.flat-1-2": { en: "1–2 Bedroom Flat", tr: "1–2 odalı daire" },
  "type.flat-3plus": { en: "3+ Bedroom Flat", tr: "3+ odalı daire" },
  "type.house": { en: "House", tr: "Müstakil ev" },
  "district.any": { en: "Any / Not sure", tr: "Fark etmez / Emin değilim" },

  // ----------------- SERVICES -----------------
  "srv.eyebrow": { en: "What we do", tr: "Ne yapıyoruz" },
  "srv.title.1": { en: "End-to-end relocation,", tr: "Baştan sona taşınma," },
  "srv.title.2": { en: "handled with care.", tr: "özenle yönetilir." },
  "srv.blurb": {
    en: "A small, dedicated team that treats your move like our own. No agencies in between, no hidden fees, no language barrier.",
    tr: "Taşınmanıza kendi taşınmamız gibi yaklaşan küçük ve özverili bir ekip. Aracı emlakçı yok, gizli ücret yok, dil engeli yok.",
  },
  "srv.search.title": { en: "Flat, Room & House Search", tr: "Daire, oda ve ev arama" },
  "srv.search.desc": {
    en: "Curated listings across Warsaw — from shared rooms to family apartments. We match you to the right landlord, district and budget.",
    tr: "Varşova'da seçilmiş ilanlar — paylaşımlı odalardan aile dairelerine. Sizi doğru ev sahibi, semt ve bütçeyle eşleştiririz.",
  },
  "srv.search.points": {
    en: ["Studios · 1–4 BR", "Short & long-term", "Pet-friendly"],
    tr: ["Stüdyo · 1–4 odalı", "Kısa & uzun süreli", "Evcil hayvan dostu"],
  },
  "srv.docs.title": { en: "Document Assistance", tr: "Evrak desteği" },
  "srv.docs.desc": {
    en: "PESEL, residence registration and lease translation — we handle the paperwork so you can focus on settling in.",
    tr: "PESEL, ikamet kaydı ve kira sözleşmesi tercümesi — evrak işleriyle biz uğraşırız, siz yerleşmeye odaklanın.",
  },
  "srv.docs.points": {
    en: ["PESEL", "Lease translation", "Residence"],
    tr: ["PESEL", "Sözleşme tercümesi", "İkamet"],
  },
  "srv.view.title": { en: "Viewings & Negotiation", tr: "Görüntüleme ve pazarlık" },
  "srv.view.desc": {
    en: "We visit with you (or for you), negotiate the rent, and make sure the contract protects you — not just the landlord.",
    tr: "Sizinle (ya da sizin yerinize) daireye gideriz, kirayı pazarlık ederiz ve sözleşmenin sizi de koruduğundan emin oluruz.",
  },
  "srv.view.points": {
    en: ["In-person", "Virtual tours", "Negotiation"],
    tr: ["Yüz yüze", "Sanal tur", "Pazarlık"],
  },
  "srv.lang.title": { en: "Speak Your Language", tr: "Sizin dilinizde" },
  "srv.lang.desc": {
    en: "Turkish, English, Azerbaijani and Polish — we'll be your voice in every meeting, call and signing.",
    tr: "Türkçe, İngilizce, Azerbaycanca ve Lehçe — her toplantı, telefon ve imza anında sesiniz biziz.",
  },
  "srv.cta_about": { en: "Hi! I'd like to know more about: {title}", tr: "Merhaba! Şu hizmet hakkında daha fazla bilgi istiyorum: {title}" },

  // ----------------- PROCESS -----------------
  "proc.eyebrow": { en: "How it works", tr: "Nasıl çalışır" },
  "proc.title.1": { en: "From first message", tr: "İlk mesajdan" },
  "proc.title.2": { en: "to keys in hand.", tr: "elinizde anahtara." },
  "proc.s1.t": { en: "Tell us what you need", tr: "Ne istediğinizi anlatın" },
  "proc.s1.b": {
    en: "Budget, neighbourhood, move-in date. A quick WhatsApp chat in your language is all it takes to start.",
    tr: "Bütçe, semt, taşınma tarihi. Kendi dilinizde kısa bir WhatsApp mesajı yeterli.",
  },
  "proc.s2.t": { en: "We shortlist & show", tr: "Liste hazırlar, gösteririz" },
  "proc.s2.b": {
    en: "You get a curated selection of real, verified listings — no fake ads, no duplicate agencies. In-person or virtual viewings.",
    tr: "Gerçek, doğrulanmış seçeneklerden oluşan kısa bir liste sunarız — sahte ilan yok, aracı emlakçı yok. Yüz yüze veya sanal tur.",
  },
  "proc.s3.t": { en: "Paperwork done right", tr: "Evraklar düzgün şekilde" },
  "proc.s3.b": {
    en: "We translate the lease, explain every clause, and handle PESEL and residence registration alongside you.",
    tr: "Kira sözleşmesini tercüme eder, her maddeyi açıklarız; PESEL ve ikamet kaydını sizinle birlikte hallederiz.",
  },
  "proc.s4.t": { en: "Move in, settle down", tr: "Taşının, yerleşin" },
  "proc.s4.b": {
    en: "Keys in your hand. We stay available for utilities, internet, and anything else you need in the first weeks.",
    tr: "Anahtarlar sizde. İlk haftalarda elektrik-su, internet ve ihtiyacınız olan her şey için yine yanınızdayız.",
  },

  // ----------------- PRICING -----------------
  "pri.eyebrow": { en: "Pricing", tr: "Ücretler" },
  "pri.title.1": { en: "Clear packages,", tr: "Net paketler," },
  "pri.title.2": { en: "personal quotes.", tr: "kişisel fiyat teklifi." },
  "pri.blurb": {
    en: "Every move is different — budget, timeline, language and paperwork all shape the final price. Pick the package that fits and we'll send a transparent quote on WhatsApp within the hour.",
    tr: "Her taşınma farklıdır — bütçe, süre, dil ve evrak işleri nihai fiyatı şekillendirir. Size uygun paketi seçin, bir saat içinde şeffaf bir fiyat teklifini WhatsApp'tan göndereyiz.",
  },
  "pri.cta": { en: "Get a personal quote", tr: "Kişisel teklif al" },
  "pri.best": { en: "Most chosen", tr: "En çok tercih edilen" },
  "pri.included": { en: "Included", tr: "Dahil olanlar" },
  "pri.not_included": { en: "Not included", tr: "Dahil olmayanlar" },
  "pri.guarantees": {
    en: "No hidden fees · Transparent quote before any work starts · First consultation is always free",
    tr: "Gizli ücret yok · Çalışmaya başlamadan önce şeffaf teklif · İlk görüşme her zaman ücretsiz",
  },
  "pri.t1.name": { en: "Property Search", tr: "Daire Arama" },
  "pri.t1.tag": { en: "Find the right flat — we do the legwork.", tr: "Doğru daireyi bulalım — koşturma bizden." },
  "pri.t1.inc": {
    en: ["Budget & district consultation", "Curated shortlist of verified listings", "In-person or virtual viewings", "Rent & deposit negotiation", "Handover & key collection"],
    tr: ["Bütçe ve semt danışmanlığı", "Doğrulanmış kısa ilan listesi", "Yüz yüze veya sanal tur", "Kira ve depozito pazarlığı", "Teslim ve anahtar alımı"],
  },
  "pri.t1.no": { en: ["Document services", "Post-move support"], tr: ["Evrak hizmetleri", "Taşınma sonrası destek"] },
  "pri.t1.msg": { en: "Hi! I'd like a personal quote for your Property Search service.", tr: "Merhaba! Daire Arama hizmetiniz için bir teklif almak istiyorum." },
  "pri.t2.name": { en: "Search + Documents", tr: "Arama + Evraklar" },
  "pri.t2.tag": { en: "Most popular for newcomers to Warsaw.", tr: "Varşova'ya yeni gelenlerin en çok tercihi." },
  "pri.t2.inc": {
    en: ["Everything in Property Search", "Lease translation & clause-by-clause review", "PESEL application assistance", "Help with residence registration (meldunek)", "Bank account & internet setup guidance"],
    tr: ["Daire Arama paketindeki her şey", "Kira sözleşmesi tercümesi ve madde madde inceleme", "PESEL başvurusu desteği", "İkamet kaydı (meldunek) yardımı", "Banka hesabı ve internet kurulum rehberliği"],
  },
  "pri.t2.no": { en: ["Residency / visa casework"], tr: ["Oturma / vize işlemleri"] },
  "pri.t2.msg": { en: "Hi! I'd like a personal quote for your Search + Documents package.", tr: "Merhaba! Arama + Evraklar paketi için bir teklif almak istiyorum." },
  "pri.t3.name": { en: "Full Relocation", tr: "Komple Taşınma" },
  "pri.t3.tag": { en: "We handle everything, you just arrive.", tr: "Her şeyi biz hallederiz, siz sadece gelin." },
  "pri.t3.inc": {
    en: ["Everything in Search + Documents", "Airport pickup on arrival day", "Utilities transfer & contracts", "Residency card appointment & accompaniment", "30 days of on-demand support after move-in"],
    tr: ["Arama + Evraklar paketindeki her şey", "Geliş gününde havaalanı karşılama", "Aboneliklerin devri ve sözleşmeleri", "Oturma kartı randevusu ve eşlik", "Taşınma sonrası 30 gün talep üzerine destek"],
  },
  "pri.t3.no": { en: [], tr: [] },
  "pri.t3.msg": { en: "Hi! I'd like a personal quote for your Full Relocation package.", tr: "Merhaba! Komple Taşınma paketi için bir teklif almak istiyorum." },

  // ----------------- WHY US -----------------
  "why.eyebrow": { en: "Why Unity", tr: "Neden Unity" },
  "why.title.1": { en: "We make Warsaw feel", tr: "Varşova'yı ilk günden" },
  "why.title.2": { en: "like home", tr: "evinizmiş gibi" },
  "why.title.3": { en: "from day one.", tr: "hissettiriyoruz." },
  "why.blurb": {
    en: "Moving to a new country is stressful enough. We take care of the paperwork, the property search and the negotiation — so you arrive ready to live, not ready to fight bureaucracy.",
    tr: "Yeni bir ülkeye taşınmak yeterince stresli. Evrak işlerini, daire aramayı ve pazarlığı biz üstleniriz; siz bürokrasiyle savaşmaya değil, yaşamaya gelin.",
  },
  "why.langs": { en: "Languages", tr: "Diller" },
  "why.r1.t": { en: "Newcomer-first", tr: "Önce yeni gelenler" },
  "why.r1.x": {
    en: "Built specifically for people moving to Warsaw — we know the pitfalls and paperwork that trip newcomers up.",
    tr: "Varşova'ya taşınanlar için tasarlandık — yeni gelenleri zorlayan tuzakları ve evrakları biliyoruz.",
  },
  "why.r2.t": { en: "No hidden fees", tr: "Gizli ücret yok" },
  "why.r2.x": {
    en: "Transparent pricing agreed upfront. No surprises at signing, ever.",
    tr: "Önceden net olarak anlaşılan şeffaf fiyat. İmza sırasında hiçbir sürpriz yok.",
  },
  "why.r3.t": { en: "Personal service", tr: "Birebir hizmet" },
  "why.r3.x": {
    en: "One point of contact from the first message to the day you get your keys — and beyond.",
    tr: "İlk mesajdan anahtarı aldığınız güne — ve sonrasına — kadar tek bir muhatap.",
  },
  "why.r4.t": { en: "Verified listings only", tr: "Sadece doğrulanmış ilanlar" },
  "why.r4.x": {
    en: "We visit and verify properties ourselves. No fake ads, no duplicate listings.",
    tr: "Daireleri kendimiz ziyaret edip doğrularız. Sahte ilan ve mükerrer kayıt yok.",
  },
  "why.cta": { en: "Start your move with us", tr: "Taşınmanıza bizimle başlayın" },

  // ----------------- TESTIMONIALS -----------------
  "tst.eyebrow": { en: "Testimonials", tr: "Müşteri yorumları" },
  "tst.title.1": { en: "300+ families placed", tr: "300+ aileye yardım ettik" },
  "tst.title.2": { en: "across 5 countries.", tr: "5 farklı ülkeden." },
  "tst.1.q": {
    en: "They spoke Turkish with me from day one. Found a beautiful flat in Mokotów in 5 days and handled my PESEL. I would have been lost without them.",
    tr: "İlk günden itibaren benimle Türkçe konuştular. 5 günde Mokotów'da güzel bir daire buldular ve PESEL'imi hallettiler. Onlar olmasa kaybolurdum.",
  },
  "tst.1.name": { en: "Ayşe K.", tr: "Ayşe K." },
  "tst.1.from": { en: "Istanbul → Warsaw", tr: "İstanbul → Varşova" },
  "tst.2.q": {
    en: "Honest, fast, and they negotiated a much better rent than I was quoted. Even helped me set up internet and register at the urząd. Highly recommended.",
    tr: "Dürüst, hızlı ve bana yapılan kira teklifinden çok daha iyisini pazarlık ettiler. İnterneti kurmamda ve urząd'a kayıtta bile yardım ettiler. Şiddetle tavsiye ederim.",
  },
  "tst.2.name": { en: "Rashad M.", tr: "Reşad M." },
  "tst.2.from": { en: "Baku → Warsaw", tr: "Bakü → Varşova" },
  "tst.3.q": {
    en: "As an English speaker I was worried about Polish leases. They translated everything clause by clause and made sure I wasn't signing anything unfair.",
    tr: "İngilizce konuşan biri olarak Lehçe kira sözleşmelerinden çekiniyordum. Her maddeyi tercüme ettiler ve adaletsiz bir şey imzalamadığımdan emin oldular.",
  },
  "tst.3.name": { en: "Emma P.", tr: "Emma P." },
  "tst.3.from": { en: "London → Warsaw", tr: "Londra → Varşova" },

  // ----------------- FAQ -----------------
  "faq.eyebrow": { en: "FAQ", tr: "SSS" },
  "faq.title.1": { en: "Moving to Warsaw —", tr: "Varşova'ya taşınmak —" },
  "faq.title.2": { en: "everything you asked.", tr: "tüm merak ettikleriniz." },
  "faq.blurb": {
    en: "Honest answers to the questions every newcomer has about deposits, PESEL, utilities and tenant rights. Still unsure? Message us — we reply in Turkish, English, Azerbaijani or Polish.",
    tr: "Yeni gelen herkesin depozito, PESEL, faturalar ve kiracı hakları konusunda merak ettiklerine dürüst cevaplar. Hâlâ emin değil misiniz? Türkçe, İngilizce, Azerbaycanca veya Lehçe mesaj atın.",
  },
  "faq.cta": { en: "Ask us on WhatsApp", tr: "WhatsApp'tan sorun" },
  "faq.cta.msg": { en: "Hi! I have a question about renting in Warsaw.", tr: "Merhaba! Varşova'da kiralama hakkında bir sorum var." },

  "faq.q1": { en: "What is PESEL and do I need one to rent?", tr: "PESEL nedir, kiralamak için gerekli mi?" },
  "faq.a1": {
    en: "PESEL is the Polish national ID number. You don't need a PESEL to sign a rental contract, but you'll need it for almost everything else — registering at a doctor, opening a bank account, signing a long-term work contract, paying taxes. We help you apply for a PESEL (CUDZOZIEMIEC-type) at your local Urząd Dzielnicy as part of our relocation service.",
    tr: "PESEL Polonya'nın ulusal kimlik numarasıdır. Kira sözleşmesi imzalamak için PESEL şart değildir; ancak doktora kayıt, banka hesabı açma, uzun süreli iş sözleşmesi ve vergiler için neredeyse her yerde gereklidir. Taşınma hizmetimizin bir parçası olarak yerel Urząd Dzielnicy'de PESEL (CUDZOZIEMIEC tipi) başvurunuzda yanınızdayız.",
  },
  "faq.q2": { en: "How much deposit should I expect to pay?", tr: "Ne kadar depozito ödemem gerekir?" },
  "faq.a2": {
    en: "In Warsaw the standard deposit (kaucja) is 1 month's rent, sometimes 2 for high-end flats or very short tenancies. Plus the first month's rent upfront. We make sure the deposit terms are written clearly in the contract before you sign anything.",
    tr: "Varşova'da standart depozito (kaucja) 1 aylık kiradır; lüks daireler veya çok kısa süreli kiralamalarda 2 ay olabilir. İlk ayın kirası da peşin ödenir. İmzalamadan önce depozito koşullarının sözleşmede net yazıldığından emin oluruz.",
  },
  "faq.q3": { en: "Are agency fees paid by me or the landlord?", tr: "Emlakçılık ücretini ben mi yoksa ev sahibi mi öder?" },
  "faq.a3": {
    en: "Unity Real Estate charges a transparent, agreed fee upfront — no hidden commissions. We'll tell you clearly what (if anything) is payable and when, before we start the search. If a landlord is paying the agency, you pay nothing to us.",
    tr: "Unity Real Estate önceden anlaşılmış, şeffaf bir ücret alır — gizli komisyon yok. Aramaya başlamadan önce, varsa, ne kadar ve ne zaman ödeyeceğinizi net olarak söyleriz. Ev sahibi emlakçıya ödeme yapıyorsa siz bize hiçbir şey ödemezsiniz.",
  },
  "faq.q4": { en: "How long does it take to find a flat in Warsaw?", tr: "Varşova'da daire bulmak ne kadar sürer?" },
  "faq.a4": {
    en: "Typically 3–10 days from the first WhatsApp to moving in. Rooms and studios move fastest. Family flats in Mokotów, Śródmieście or Wilanów can take 2–3 weeks if you have specific requirements.",
    tr: "İlk WhatsApp mesajından taşınmaya genellikle 3–10 gün. Odalar ve stüdyolar daha hızlı kapanır. Mokotów, Śródmieście veya Wilanów'da özel istekleri olan aile daireleri 2–3 hafta sürebilir.",
  },
  "faq.q5": { en: "Can I rent without a Polish work contract or without being in Poland yet?", tr: "Polonya iş sözleşmem yokken ya da daha gelmemişken kiralayabilir miyim?" },
  "faq.a5": {
    en: "Yes. Many of our clients sign their first lease before arriving in Warsaw. Landlords usually ask for proof of income, a passport, and a credible reference. We help translate and present your documents so the landlord is comfortable.",
    tr: "Evet. Birçok müşterimiz ilk kiralarını Varşova'ya gelmeden imzalar. Ev sahipleri genellikle gelir belgesi, pasaport ve güvenilir bir referans ister. Belgelerinizi tercüme edip sunmanızda yardımcı oluruz.",
  },
  "faq.q6": { en: "What are my rights as a tenant in Poland?", tr: "Polonya'da kiracı olarak haklarım nelerdir?" },
  "faq.a6": {
    en: "Polish rental law protects tenants strongly. Rent cannot be raised arbitrarily, the landlord cannot enter without notice, and the deposit must be returned within 1 month of move-out minus documented damages. We review every clause of your contract before you sign.",
    tr: "Polonya kira hukuku kiracıyı güçlü biçimde korur. Kira keyfi olarak artırılamaz, ev sahibi habersiz giremez ve depozito çıkıştan sonra 1 ay içinde — belgelenmiş hasarlar düşülerek — iade edilmek zorundadır. Sözleşmenizdeki her maddeyi imzalamadan önce inceleriz.",
  },
  "faq.q7": { en: "Who pays for utilities — water, electricity, gas, internet?", tr: "Faturaları (su, elektrik, gaz, internet) kim öder?" },
  "faq.a7": {
    en: "Usually rent includes the administrative fee (czynsz administracyjny — covers building maintenance, sometimes water/heating) and you pay electricity and internet separately. We always check exactly what's included before you sign.",
    tr: "Genellikle kiraya yönetim ücreti (czynsz administracyjny — bina bakımı, bazen su/ısıtma) dahildir; elektrik ve interneti ayrı ödersiniz. İmzalamadan önce neyin dahil olduğunu mutlaka kontrol ederiz.",
  },
  "faq.q8": { en: "Can you help me find a replacement tenant for my lease?", tr: "Kiranızı devralacak yeni bir kiracı bulmama yardım eder misiniz?" },
  "faq.a8": {
    en: "Yes — this is one of our most frequent requests. We'll market the flat, screen replacement tenants, handle viewings in your language, and coordinate the handover with your landlord so your deposit is protected.",
    tr: "Evet — bu en sık aldığımız taleplerden biri. Daireyi yayınlar, adayları eleriz, görüntülemeleri kendi dilinizde yönetiriz ve ev sahibinizle teslim sürecini koordine ederiz; böylece depozitonuz güvende olur.",
  },
  "faq.q9": { en: "I already live in Warsaw but want to change flats — can you still help?", tr: "Zaten Varşova'da yaşıyorum ama daire değiştirmek istiyorum — yine de yardım eder misiniz?" },
  "faq.a9": {
    en: "Absolutely. We know the market, the building managers and the fair rental prices by district — so we can often find you a better flat faster than searching alone, and help you exit your current lease cleanly.",
    tr: "Kesinlikle. Pazarı, bina yöneticilerini ve semt bazında adil kira fiyatlarını biliriz; tek başınıza aramaya göre daha hızlı daha iyi bir daire bulup mevcut kiranızdan temiz çıkmanıza yardım ederiz.",
  },
  "faq.q10": { en: "Do you only help Turkish and Azerbaijani clients?", tr: "Sadece Türk ve Azerbaycanlı müşterilere mi yardım ediyorsunuz?" },
  "faq.a10": {
    en: "No — we help anyone relocating to Warsaw. We speak Turkish, English, Azerbaijani and Polish fluently. Most of our clients are Turkish and Azerbaijani, but we work with newcomers from 5 different countries.",
    tr: "Hayır — Varşova'ya taşınan herkese yardım ediyoruz. Türkçe, İngilizce, Azerbaycanca ve Lehçeyi akıcı konuşuyoruz. Müşterilerimizin çoğu Türk ve Azerbaycanlı olsa da 5 farklı ülkeden gelen kişilerle çalışıyoruz.",
  },

  // ----------------- CONTACT -----------------
  "ctc.eyebrow": { en: "Let's talk", tr: "Konuşalım" },
  "ctc.title.1": { en: "Ready to find", tr: "Kendi" },
  "ctc.title.2": { en: "your Warsaw?", tr: "Varşova'nızı bulmaya hazır mısınız?" },
  "ctc.blurb": {
    en: "Send us a WhatsApp message in Turkish, English, Azerbaijani or Polish. We usually reply within the hour.",
    tr: "Bize Türkçe, İngilizce, Azerbaycanca veya Lehçe WhatsApp mesajı gönderin. Genellikle bir saat içinde döneriz.",
  },
  "ctc.cta_wa": { en: "Message on WhatsApp", tr: "WhatsApp'tan mesaj at" },
  "ctc.cta_call": { en: "Call {phone}", tr: "Ara {phone}" },
  "ctc.phone": { en: "Phone / WhatsApp", tr: "Telefon / WhatsApp" },
  "ctc.based": { en: "Based in", tr: "Konum" },
  "ctc.based_val": { en: "Warsaw, Poland", tr: "Varşova, Polonya" },
  "ctc.reply": { en: "Reply time", tr: "Cevap süresi" },
  "ctc.reply_val": { en: "Usually within 1 hour", tr: "Genellikle 1 saat içinde" },

  // ----------------- FOOTER -----------------
  "ftr.tag": {
    en: "Helping newcomers find homes and settle in Warsaw — with care, in your own language.",
    tr: "Varşova'ya gelenlerin ev bulup yerleşmesine yardım ediyoruz — özenle, kendi dilinizde.",
  },
  "ftr.explore": { en: "Explore", tr: "Keşfet" },
  "ftr.contact": { en: "Get in touch", tr: "İletişime geçin" },
  "ftr.warsaw": { en: "Warsaw, Poland", tr: "Varşova, Polonya" },
  "ftr.rights": { en: "All rights reserved.", tr: "Tüm hakları saklıdır." },
  "ftr.chat": { en: "WhatsApp chat", tr: "WhatsApp sohbeti" },

  // ----------------- FLOATING WA -----------------
  "fwa.tip.q": { en: "Need a flat in Warsaw?", tr: "Varşova'da daire mi lazım?" },
  "fwa.tip.a": { en: "Ask us in your language.", tr: "Kendi dilinizde sorun." },
  "fwa.aria": { en: "Chat on WhatsApp", tr: "WhatsApp'tan yaz" },

  // ----------------- TESTIMONIALS EXTRAS -----------------
  "tst.rating.score": { en: "4.9 / 5", tr: "4.9 / 5" },
  "tst.rating.sub":   { en: "based on 87 WhatsApp & in-person reviews", tr: "87 WhatsApp ve yüz yüze yorumuna göre" },
  "tst.verified":     { en: "Verified via WhatsApp", tr: "WhatsApp ile doğrulandı" },
  "tst.4.q": {
    en: "I moved from Baku without knowing a single word of Polish. They met me at the airport, took me to viewings the same week and translated the contract line by line.",
    tr: "Bakü'den tek kelime Lehçe bilmeden geldim. Beni havaalanında karşıladılar, aynı hafta görüntülemelere götürdüler ve sözleşmeyi satır satır çevirdiler.",
  },
  "tst.4.name": { en: "Rashad A.", tr: "Rəşad A." },
  "tst.4.from": { en: "Baku → Mokotów", tr: "Bakü → Mokotów" },

  // Default WhatsApp message
  "wa.default": {
    en: "Hi! I'd like help finding a flat in Warsaw.",
    tr: "Merhaba! Varşova'da daire bulmak için yardıma ihtiyacım var.",
  },
};

const LangContext = createContext({ lang: "tr", setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem("unity-lang") || detectLang();
    } catch {
      return detectLang();
    }
  });

  const setLang = (next) => {
    setLangState(next);
    try {
      localStorage.setItem("unity-lang", next);
    } catch {}
    document.documentElement.setAttribute("lang", next);
  };

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const t = (key, vars) => {
    const entry = DICT[key];
    let value = EXTRA[lang]?.[key] ?? (entry ? entry[lang] ?? entry.en ?? key : key);
    if (typeof value === "string" && vars) {
      for (const [k, v] of Object.entries(vars)) {
        value = value.replace(new RegExp(`{${k}}`, "g"), v);
      }
    }
    return value;
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useT() {
  return useContext(LangContext);
}

export function waLinkT(t, customMessage) {
  const text = customMessage ?? t("wa.default");
  // Phone number is in brand.js
  return `https://wa.me/48786660277?text=${encodeURIComponent(text)}`;
}
